/** Law-firm user / admin dashboard + home section copy keyed by variant. */

export const LF_HOME_VARIANT = Object.freeze({
  user: "user",
  admin: "admin",
});

const LF_SHARED_COPY = {
  sections: {
    activity: true,
    assignmentAlerts: true,
    indemnity: true,
    drafts: true,
  },
  activity: {
    title: "Recent Activity",
    subtitle: "Your latest interactions and updates",
    emptyActivity: "No recent activity.",
    seeMore: "See more",
    loadMoreLabel: "See more",
    viewBond: "View Bond",
    goBack: "Go back",
  },
  assignment: {
    title: "Assignment Alerts",
    subtitle: "Bonds missing attorney assignment",
    emptyList: "No bonds with missing assignments.",
    view: "View",
  },
  indemnity: {
    title: "Pending Signatures",
    subtitle: "Bonds with pending signed indemnity agreements",
    emptyList: "No bonds with Pending Signatures.",
    view: "View",
    markCompleted: "Mark as completed",
  },
  drafts: {
    title: "Draft Bond Requests",
    subtitle: "Continue working on your saved drafts.",
    emptyDrafts: "You have no saved bond requests.",
    resume: "Resume",
    delete: "Delete",
  },
};

const VARIANT_META = {
  [LF_HOME_VARIANT.user]: {
    dashboardTitle: "Law Firm User Dashboard",
    showAddFirmWorkflow: true,
    ...LF_SHARED_COPY,
  },
  [LF_HOME_VARIANT.admin]: {
    dashboardTitle: "Law Firm Admin Dashboard",
    showAddFirmWorkflow: false,
    ...LF_SHARED_COPY,
  },
};

/**
 * @param {'user'|'admin'} variant
 */
export function getLawFirmHomeVariantConfig(variant) {
  return VARIANT_META[variant] || VARIANT_META[LF_HOME_VARIANT.user];
}

const BOND_OPERATIONS_VALUES = [
  { name: "Request a Bond", path: "/bond-creation-outsider", iconKey: "postAdd" },
  { name: "Cancel Bonds", path: "/vermost_cancel_bonds_lawfirm", iconKey: "cancel" },
  { name: "Endorse Bonds", path: "/vermost_endorse_bonds_lawfirm", iconKey: "edit" },
  { name: "View Bonds", path: "/vermost_approve_bonds_lawfirm", iconKey: "visibility" },
  { name: "Upload Indemnity", path: "/vermost_manually_signed_indemnities", iconKey: "postAdd" },
];

/**
 * Link metadata for LawFirmDashboard (`iconKey`: map in consumer to `@mui/icons-material`).
 * @param {'user'|'admin'} variant
 * @param {number|string|null|undefined} lawFirmId
 */
export function buildLawFirmDashboardLinkGroups(variant, lawFirmId) {
  const bondOperations = {
    action_value: "Bond Operations",
    action_description:
      variant === LF_HOME_VARIANT.admin
        ? "Create new bond requests and review existing submissions for your firm."
        : "Covers end-to-end surety bond workflows such as creating new requests, retrieving bond records, processing cancellations, and managing endorsements.",
    values: BOND_OPERATIONS_VALUES,
  };

  if (variant === LF_HOME_VARIANT.admin) {
    return [
      bondOperations,
      {
        action_value: "Law Firm Administration",
        action_description:
          "Configure firm details and access core tools used by operations.",
        values: [
          {
            name: "Manage Law Firm",
            path: `/lawfirmdetails/${lawFirmId ?? ""}`,
            iconKey: "business",
          },
          { name: "Premium Calculator", path: "/premium_calculator", iconKey: "calculate" },
          {
            name: "Enhancement Requests",
            path: "/create-enhancement-request",
            iconKey: "autoAwesome",
          },
          {
            name: "Access Requests",
            path: "/law-firm/access-requests",
            iconKey: "business",
          },
        ],
      },
    ];
  }

  return [
    bondOperations,
    {
      action_value: "Operations Hub",
      action_description:
        "Centralized access to essential firm tools — calculate premiums, track improvements, and manage operational requests in one place.",
      values: [
        { name: "Premium Calculator", path: "/premium_calculator", iconKey: "calculate" },
        {
          name: "Enhancement Requests",
          path: "/create-enhancement-request",
          iconKey: "autoAwesome",
        },
      ],
    },
  ];
}
